export type SummaryFieldType = "text" | "date" | "checkbox" | "checkboxGroup" | "file";

export interface SummaryFieldDefinition {
  name: string;
  label: string;
  type?: SummaryFieldType;
}

export interface SummarySectionDefinition {
  title: string;
  fields: SummaryFieldDefinition[];
}

export interface SubmissionAnswer {
  label: string;
  value: string;
}

export interface SubmissionSummarySection {
  title: string;
  answers: SubmissionAnswer[];
}

export interface SubmissionSummary {
  businessName: string;
  formTitle: string;
  clientName: string;
  eventName: string;
  eventNameLabel?: string;
  eventDate: string;
  eventDateLabel?: string;
  submittedAt: string;
  sections: SubmissionSummarySection[];
}

export function buildSubmissionSummary(options: {
  formData: FormData;
  formTitle: string;
  clientNameField: string;
  eventNameField: string;
  eventDateField?: string;
  eventNameLabel?: string;
  eventDateLabel?: string;
  sections: SummarySectionDefinition[];
}) {
  const sections = options.sections
    .map((section) => ({
      title: section.title,
      answers: section.fields
        .map((field) => fieldToAnswer(options.formData, field))
        .filter((answer): answer is SubmissionAnswer => Boolean(answer)),
    }))
    .filter((section) => section.answers.length > 0);

  return {
    businessName: "Kreatethedate",
    formTitle: options.formTitle,
    clientName: textValue(options.formData, options.clientNameField) || "Not provided",
    eventName: textValue(options.formData, options.eventNameField) || "Not provided",
    eventNameLabel: options.eventNameLabel,
    eventDate: options.eventDateField ? formatDateValue(textValue(options.formData, options.eventDateField)) || "Not provided" : "",
    eventDateLabel: options.eventDateLabel,
    submittedAt: formatDateTime(new Date()),
    sections,
  } satisfies SubmissionSummary;
}

function fieldToAnswer(formData: FormData, field: SummaryFieldDefinition) {
  if (field.type === "checkbox") {
    return formData.get(field.name) ? { label: field.label, value: "Confirmed" } : null;
  }

  if (field.type === "checkboxGroup") {
    const values = formData
      .getAll(field.name)
      .filter((entry): entry is string => typeof entry === "string")
      .map((value) => value.trim())
      .filter(Boolean);
    return values.length ? { label: field.label, value: values.join(", ") } : null;
  }

  if (field.type === "file") {
    const fileNames = formData
      .getAll(field.name)
      .filter((entry): entry is File => entry instanceof File && entry.size > 0)
      .map((file) => file.name);
    return fileNames.length ? { label: field.label, value: fileNames.join(", ") } : null;
  }

  const value = textValue(formData, field.name);
  if (!value) return null;

  return {
    label: field.label,
    value: field.type === "date" ? formatDateValue(value) || value : value,
  };
}

function textValue(formData: FormData, fieldName: string) {
  const value = formData.get(fieldName);
  return typeof value === "string" ? value.trim() : "";
}

function formatDateValue(value: string) {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return value;

  const [, year, month, day] = match;
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(Number(year), Number(month) - 1, Number(day)));
}

function formatDateTime(date: Date) {
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}
